import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp?: string;
  countryCode: string;
  subject: string;
  message: string;
  packageInterest?: string;
}

interface Package {
  _id: string;
  name: string;
  priceAUD: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
}

const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'Office',
      details: ['Jalan By Pass Ngurah Rai No. 61, Suwung Kauh', 'Denpasar 80221, Bali, Indonesia'],
      
    },
    {
      icon: EnvelopeIcon,
      title: 'Email Us',
      details: ['enquiry@baligetaway.com.au'],
      
    },
    {
      icon: ClockIcon,
      title: 'Business Hours',
      details: ['Monday - Sunday', '9:00 AM - 10:00 PM (GMT+8)'],
      
    }
  ];

  const faqs = [
    {
      question: 'How does the voucher system work?',
      answer: 'Purchase your voucher now and use it within the validity period. After purchase, our team contacts you to help select your resort and dates. You have complete flexibility to choose when and where to stay within your package options.'
    },
    {
      question: 'Can I change my resort selection after booking?',
      answer: 'Yes! As long as your voucher hasn\'t been redeemed and you\'re within the validity period, you can change your resort selection. Some packages may have restrictions, so please contact our team for assistance.'
    },
    {
      question: 'What happens if I need to cancel?',
      answer: 'Cancellation policies vary by package and timing. Generally, vouchers are fully refundable within 48 hours of purchase. After that, cancellation terms depend on how close to your travel dates you cancel.'
    },
    {
      question: 'Are meals included in the packages?',
      answer: 'Meal inclusions vary by package and resort. Most packages include breakfast, while some luxury packages include all meals. Check individual package details or contact us for specific information.'
    },
    {
      question: 'Do you help with flight bookings?',
      answer: 'While we don\'t book flights directly, our team can recommend trusted travel agents and provide advice on the best times to visit Bali based on your preferences.'
    }
  ];

  // Fetch packages on component mount
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get('/api/packages?active=true');
        const allPackages = response.data.packages || [];
        
        // Filter only live and active packages
        const livePackages = allPackages.filter((pkg: Package) => 
          pkg.name && (pkg as any).isLive && (pkg as any).isActive
        );
        
        setPackages(livePackages);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        // Don't show error to user, just leave packages empty
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchPackages();
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      // Send to contact enquiries API (separate from package enquiries)
      const response = await axios.post('/api/contact/enquiry', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        whatsapp: data.whatsapp,
        countryCode: data.countryCode,
        subject: data.subject,
        message: data.message,
        packageInterest: data.packageInterest,
        type: 'contact_form'
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
        reset();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Bali Getaway - Get Expert Travel Assistance</title>
        <meta name="description" content="Get in touch with our Bali travel experts. We're here to help you plan your perfect getaway with personalized assistance and local insights." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white container-max px-4">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            Contact Us
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Let's plan your perfect Bali adventure together
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-display text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our travel experts are here to help you create unforgettable memories
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="card p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <info.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">{info.title}</h3>
                <div className="space-y-1 mb-4">
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-gray-600">{detail}</p>
                  ))}
                </div>
                
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="max-w-2xl mx-auto">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h3 className="text-3xl font-bold font-display text-gray-900 mb-4">
                  Send Us a Message
                </h3>
                <p className="text-gray-600">
                  Have questions about our packages? Want to customize your experience? 
                  We're here to help make your Bali dreams come true.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register('firstName', { required: 'First name is required' })}
                      className={`form-input ${errors.firstName ? 'border-red-500' : ''}`}
                      placeholder="John"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      {...register('lastName', { required: 'Last name is required' })}
                      className={`form-input ${errors.lastName ? 'border-red-500' : ''}`}
                      placeholder="Doe"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <select
                      {...register('countryCode', { required: 'Please select your country' })}
                      className={`form-select ${errors.countryCode ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select Country</option>
                      <option value="+61">🇦🇺 Australia (+61)</option>
                      <option value="+1">🇺🇸 United States (+1)</option>
                      <option value="+1">🇨🇦 Canada (+1)</option>
                      <option value="+44">🇬🇧 United Kingdom (+44)</option>
                      <option value="+62">🇮🇩 Indonesia (+62)</option>
                      <option value="+65">🇸🇬 Singapore (+65)</option>
                      <option value="+60">🇲🇾 Malaysia (+60)</option>
                      <option value="+66">🇹🇭 Thailand (+66)</option>
                      <option value="+84">🇻🇳 Vietnam (+84)</option>
                      <option value="+63">🇵🇭 Philippines (+63)</option>
                      <option value="+64">🇳🇿 New Zealand (+64)</option>
                      <option value="+81">🇯🇵 Japan (+81)</option>
                      <option value="+82">🇰🇷 South Korea (+82)</option>
                      <option value="+86">🇨🇳 China (+86)</option>
                      <option value="+91">🇮🇳 India (+91)</option>
                      <option value="+49">🇩🇪 Germany (+49)</option>
                      <option value="+33">🇫🇷 France (+33)</option>
                      <option value="+39">🇮🇹 Italy (+39)</option>
                      <option value="+34">🇪🇸 Spain (+34)</option>
                      <option value="+31">🇳🇱 Netherlands (+31)</option>
                    </select>
                    {errors.countryCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.countryCode.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      {...register('phone', { required: 'Phone number is required' })}
                      className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                      placeholder="123 456 7890"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number (Optional)
                  </label>
                  <input
                    type="tel"
                    {...register('whatsapp')}
                    className="form-input"
                    placeholder="Same as phone number or different WhatsApp number"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    💬 If different from phone number above, please include country code (e.g., +61 123 456 7890)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    {...register('subject', { required: 'Please select a subject' })}
                    className={`form-select ${errors.subject ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select a subject</option>
                    <option value="package-inquiry">Package Inquiry</option>
                    <option value="booking-help">Booking Assistance</option>
                    <option value="custom-package">Custom Package Request</option>
                    <option value="support">General Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package Interest (Optional)
                  </label>
                  <select
                    {...register('packageInterest')}
                    className="form-select"
                    disabled={loadingPackages}
                  >
                    <option value="">
                      {loadingPackages ? 'Loading packages...' : 'Select a package'}
                    </option>
                    {packages.map((pkg) => {
                      // Check for active last minute deal
                      const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
                      const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
                      const isDealActive = pkg.lastMinuteDealExpiryDate 
                        ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() 
                        : false;
                      
                      const displayPrice = hasDealPrice && isDealActive && dealPriceAUD 
                        ? dealPriceAUD 
                        : pkg.priceAUD;
                      
                      const dealLabel = hasDealPrice && isDealActive ? ' ⚡ DEAL' : '';
                      
                      return (
                        <option key={pkg._id} value={pkg._id}>
                          {pkg.name} - A${displayPrice.toLocaleString()}{dealLabel}
                        </option>
                      );
                    })}
                  </select>
                  {loadingPackages && (
                    <p className="mt-1 text-sm text-gray-500">Loading available packages...</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    {...register('message', { required: 'Message is required' })}
                    rows={6}
                    className={`form-input ${errors.message ? 'border-red-500' : ''}`}
                    placeholder="Tell us about your dream Bali vacation..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner w-5 h-5"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-5 w-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {/* <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-display text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find quick answers to common questions about our services
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="card p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">
                Can't find what you're looking for?
              </p>
              <button className="btn-secondary">
                View All FAQs
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="container-max text-center">
          <h2 className="text-4xl font-bold font-display mb-4">
            Ready to Start Planning?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don't wait! The best Bali experiences are just a message away. 
            Contact us today and let's create your perfect getaway.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+6236112344567" 
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Call Us Now
            </a>
            <a 
              href="mailto:hello@baligetaway.com" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Send Email
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
